/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChartLineIcon } from '@rsmus/myrsm-icons'
import { tokens } from 'src/styles/materialTheme'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import { Styles } from '../../types'

/**
 * Props interface for the ConfirmRemoveReportInstanceDialog component.
 * Contains all necessary properties to control the dialog's open state,
 * handle user actions, and display instance-specific information.
 */
interface ConfirmRemoveReportInstanceDialogProps {
  /** Controls whether the dialog is visible */
  open: boolean
  /** Callback invoked when the user cancels or closes the dialog */
  onClose: () => void
  /** Callback invoked when the user confirms the removal action */
  onConfirm: () => void
  /** The display name of the PBI Report Instance being removed */
  instanceDisplayName?: string
  /** The name of the parent PBI Report Definition */
  reportDefinitionName?: string
}

/**
 * Styles object for the confirmation dialog components.
 * Follows the established RSM design patterns with consistent spacing,
 * typography, and color token usage.
 */
const styles: Styles = {
  iconCell: {
    display: 'flex',
    justifyContent: 'center',
    border: 0,
    paddingTop: '1.5rem',
  },
  title: {
    fontFamily: 'Prelo, sans-serif',
    color: tokens.colors.rsmGray.copy,
    fontWeight: 300,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    border: 0,
    padding: '0.5rem',
  },

  subtitle: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    color: 'text.secondary',
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },

  names: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },
  actionsCell: {
    border: 0,
    paddingTop: '2rem',
    paddingBottom: '1rem',
  },
  actions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.625rem',
  },
  iconWrap: {
    width: '7.5rem',
    height: '7.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '7.5rem',
      height: '7.5rem',
    },
  },
}

/**
 * ConfirmRemoveReportInstanceDialog - A confirmation dialog component for deleting PBI Report Instance records.
 *
 * This component displays a modal dialog asking the user to confirm the removal of a PBI Report Instance.
 * It shows the instance's display name and its parent report definition name to help users identify
 * the specific instance being removed.
 *
 * Features:
 * - Accessible aria-labels for screen reader support
 * - Data-testid attributes for E2E testing
 * - Localized text using i18next
 * - Consistent RSM branding with ChartLineIcon
 * - Cancel and Confirm action buttons
 *
 * @param props - The component props
 * @returns A React component rendering the confirmation dialog
 */
const ConfirmRemoveReportInstanceDialog = ({
  open,
  onClose,
  onConfirm,
  instanceDisplayName,
  reportDefinitionName,
}: ConfirmRemoveReportInstanceDialogProps) => {
  const { t } = useTranslation()
  const desktopMaxWidth = '41rem'
  const desktopMaxHeight = '30rem'

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={onClose}
        aria-label={t('PowerBI.RemoveReportInstanceModal.Title')}
        desktopMaxHeight={desktopMaxHeight}
        desktopMaxWidth={desktopMaxWidth}
        removeHeaderSpacing
        data-testid="Lbl_PowerBI_RemoveInstanceDialog_Modal">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={styles.iconCell}>
                <Box sx={styles.iconWrap}>
                  <ChartLineIcon />
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={styles.title}
                data-testid="Tbl_PowerBI_RemoveInstanceDialog_Title">
                {t('PowerBI.RemoveReportInstanceModal.Header')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={styles.subtitle}
                data-testid="Tbl_PowerBI_RemoveInstanceDialog_Subtitle">
                {t('PowerBI.RemoveReportInstanceModal.SubHeader')}
              </TableCell>
            </TableRow>

            {(instanceDisplayName || reportDefinitionName) && (
              <TableRow>
                <TableCell
                  sx={styles.names}
                  data-testid="Tbl_PowerBI_RemoveInstanceDialog_InstanceNames">
                  {instanceDisplayName} – {reportDefinitionName}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell sx={styles.actionsCell}>
                <Stack direction="row" sx={styles.actions}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    data-testid="Btn_PowerBI_RemoveInstanceDialog_Cancel">
                    {t('PowerBI.RemoveReportInstanceModal.Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={onConfirm}
                    data-testid="Btn_PowerBI_RemoveInstanceDialog_Confirm">
                    {t('PowerBI.RemoveReportInstanceModal.Confirm')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ResponsiveModal>
    </Box>
  )
}

export default ConfirmRemoveReportInstanceDialog
